import { yupResolver } from '@hookform/resolvers/yup';
import type { SavedHolder } from 'api/checkoutpage/savedHolders';
import { useCartContext } from 'context/CartContext/CartContext';
import useHylideBridge from 'hooks/useHylideBridge';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DIALING_CODE_IRAQ } from 'utils/constants';
import { normalizeIraqiPhone } from 'utils/iraqiPhone';
import { isSuperQi } from 'utils/miniAppPlatform';
import { fullNameSchema, iraqiPhoneNumberSchema } from 'utils/validation';
import * as yup from 'yup';
import { useSavedHolders } from '../hooks/useSavedHolders';
import { HolderCard } from './HolderCard';
import { SavedHoldersSheet } from './SavedHoldersSheet';

/**
 * Where a picked contact comes from. The checkout only ever runs in one of two environments,
 * so this is a plain two-branch resolve — no provider/strategy abstraction:
 * - `device`       → SuperQi native picker (Hylide `choosePhoneContact`).
 * - `savedHolders` → backend saved-holders bottom sheet (web/default).
 */
export type ContactSource = 'device' | 'savedHolders';

/** Unified handoff shape every contact source collapses to before filling a holder card. */
export type PickedContact = { name?: string; phone?: string };

/**
 * Where a holder's details actually came from, carried per holder through to submit (TZ-CO-6).
 *
 * `manual` is the default and the floor: a holder only leaves it by being filled from a picker, and
 * any subsequent hand-edit returns it here. Analytics-only — it does not change validation or the
 * money path.
 */
export const HOLDER_SOURCES = ['manual', 'device', 'saved'] as const;
export type HolderSource = (typeof HOLDER_SOURCES)[number];

export type FormValues = yup.InferType<typeof schema>;

const schema = yup.object().shape({
  ticketHolders: yup
    .array()
    .of(
      yup.object().shape({
        name: fullNameSchema,
        phoneNumber: iraqiPhoneNumberSchema,
        source: yup.mixed<HolderSource>().oneOf(HOLDER_SOURCES).required().default('manual'),
        // Whether to remember this holder for the buyer's next booking. Per-holder and **on by
        // default**, as TZ-CO-6 specifies — but with **no control in the current design**, where
        // "حفظ التفاصيل" is the button that collapses the card, not a persistence preference. So
        // this is constant `true` today: the intent the deferred BE write half will read, ready
        // for design to add an opt-out. See the `TODO` in `useCheckoutSubmission`.
        saveDetails: yup.boolean().required().default(true),
      }),
    )
    .required(),
});

interface TicketHolderFormProps {
  onSubmit: (data: FormValues) => void;
  /** Opens the login modal when the saved-holders request comes back `401` (see `usePromoCode`). */
  onAuthRequired?: () => void;
}

export const TicketHolderForm = ({ onSubmit, onAuthRequired }: TicketHolderFormProps) => {
  const dialingCode = localStorage.getItem('dialingCode') || DIALING_CODE_IRAQ;
  const { tickets } = useCartContext();
  const hylideBridge = useHylideBridge();

  // Resolve the contact source once, inline. SuperQi hosts expose a native device picker;
  // every other build (web/default) uses the backend saved-holders sheet. The holder card
  // renders identically either way — only the button label and which picker opens differ.
  const contactSource: ContactSource = isSuperQi() ? 'device' : 'savedHolders';

  // Whether the resolved source can actually open something. A visible button that does nothing
  // when tapped is worse than no button at all, so the card falls back to manual-entry-only when
  // the picker is unavailable — a host without the Hylide bridge, or an older one missing
  // `choosePhoneContact` (TZ-CO-18). The saved-holders sheet always opens: it has a loading, a
  // populated and an empty state, so tapping the button is never a no-op even for a buyer with no
  // saved holders. Manual entry is present in every case, so the purchase is never blocked.
  const canPickContact =
    contactSource === 'device' ? Boolean(hylideBridge?.choosePhoneContact) : true;

  // Per-holder UI state: a holder shows the compact "filled" card once its details are saved or
  // fully picked; "Change" re-expands it. Only `collapseIfValid` may set an entry to `true`, so a
  // collapsed card always holds a valid holder.
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

  // Which holder card the (single, shared) saved-holders sheet is currently open for; `null` while
  // it is closed. One sheet instance serves every card, so the index is what routes the pick back.
  const [sheetHolderIndex, setSheetHolderIndex] = useState<number | null>(null);

  // Fetched as soon as the page loads, not on first sheet-open: `me` fills ticket 1 before the
  // buyer touches anything, and the same cached response serves every later sheet-open.
  const {
    me,
    holders: savedHolders,
    isLoading: isLoadingSavedHolders,
  } = useSavedHolders({ onAuthRequired });

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    // Seeded per ticket rather than relying on each `Controller`'s own `defaultValue`, because
    // `source` and `saveDetails` have no input of their own on an untouched card — without this
    // they would be absent from the submitted values instead of `manual` / on-by-default.
    defaultValues: {
      ticketHolders: tickets.map(() => ({
        name: '',
        phoneNumber: '',
        source: 'manual' as const,
        saveDetails: true,
      })),
    },
  });

  // **The one place a card is allowed to collapse.** Validates the holder's fields and collapses
  // only when both pass, because the compact view renders name + phone with nowhere to show an
  // inline error — collapsing an invalid holder hides its error and makes submit fail silently
  // (TZ-CO-16). All three routes in go through here: the "حفظ التفاصيل" button on a hand-typed
  // card, the device pick, and the saved-holders sheet.
  const collapseIfValid = async (index: number) => {
    const isValid = await trigger([
      `ticketHolders.${index}.name` as const,
      `ticketHolders.${index}.phoneNumber` as const,
    ]);
    if (isValid) {
      setCollapsed((prev) => ({ ...prev, [index]: true }));
    }
  };

  // Single fill path shared by both sources: takes the unified `{ name, phone }` (phone already
  // normalized by the caller) and writes whatever it got to the holder's fields, tagging where it
  // came from.
  //
  // A partial pick — a contact with a name but no usable phone — prefills what it can and leaves
  // the manual form open on the missing field, rather than discarding the pick entirely. It is
  // still tagged with the picker's source: the name genuinely came from there, and the tag returns
  // to `manual` the moment the buyer types (see `markHolderEditedByHand`).
  const fillHolderFromContact = async (
    index: number,
    { name, phone }: PickedContact,
    source: Exclude<HolderSource, 'manual'>,
  ) => {
    if (name) {
      setValue(`ticketHolders.${index}.name` as const, name, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    if (phone) {
      setValue(`ticketHolders.${index}.phoneNumber` as const, phone, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    if (name || phone) {
      setValue(`ticketHolders.${index}.source` as const, source);
    }
    if (name && phone) {
      await collapseIfValid(index);
    }
  };

  // **Ticket 1 is assigned to the buyer themselves**, as soon as `me` arrives: the common case is
  // buying a ticket for yourself, so the first card is filled and the buyer only picks holders for
  // the remaining tickets.
  //
  // Runs at most once, and never overwrites the buyer. `me` arrives asynchronously, so by then they
  // may already have typed into card 1 or filled it from the sheet — either way their input wins.
  // The ref latches even when the pristine check declines, so a later render cannot retry and
  // clobber them.
  //
  // `me.name` is nullable by contract, and the live endpoint does return `null` for a buyer who has
  // never given one. In that case only the **phone** is prefilled and the card stays on the form for
  // the buyer to type their name — the partial-pick behaviour from TZ-CO-16. A placeholder name is
  // not an option here: see the note at the fill site.
  //
  // Tagged `saved` because it came from the saved-holders endpoint — `HOLDER_SOURCES` is pinned by
  // the RFC, so this does not invent a fourth source for it.
  const hasAssignedMe = useRef(false);

  // Card 1 shows a skeleton rather than an empty form while its contact is on the way — offering
  // fields and then overwriting them is the worse of the two. `isLoading` is false whenever no
  // request is in flight (signed out, disabled, already resolved, or failed), so this cannot hang.
  const isAwaitingMe = isLoadingSavedHolders && !hasAssignedMe.current;

  useEffect(() => {
    if (hasAssignedMe.current || !me || !tickets.length) return;
    hasAssignedMe.current = true;

    const first = getValues('ticketHolders.0');
    if (first?.name || first?.phoneNumber) return;

    const normalized = normalizeIraqiPhone(me.phone);
    void fillHolderFromContact(
      0,
      {
        // Only a real name. `holder.name` is submitted as `holderName` and persisted — it goes on
        // the ticket, into the WhatsApp delivery, and (once the BE write half lands) into the
        // buyer's saved holders. A placeholder like "التذكرة الخاصة بي" would be stored as if it
        // were a person, and would then fail `fullNameSchema` on read, so the saved holder it
        // created could never be reused.
        //
        // `?.trim() ||` rather than `??`: an empty or whitespace-only name from the API is as
        // unusable as a null one, and would otherwise be written straight into the field.
        name: me.name?.trim() || undefined,
        phone: normalized.ok ? normalized.national : undefined,
      },
      'saved',
    );
    // `fillHolderFromContact` and `getValues` are stable for this purpose; `me` is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Any hand-edit of a name or phone returns that holder to `manual`, whatever filled it first —
  // once the buyer has retyped part of it, the value is no longer the picker's.
  //
  // Only the two text fields call this. `setValue` does not fire a field's `onChange`, so a
  // programmatic fill cannot be mistaken for typing; and the "حفظ التفاصيل" toggle deliberately
  // does not reset the source, since flipping a preference is not editing the holder.
  const markHolderEditedByHand = (index: number) => {
    if (getValues(`ticketHolders.${index}.source` as const) !== 'manual') {
      setValue(`ticketHolders.${index}.source` as const, 'manual');
    }
  };

  // Success from the SuperQi native picker: take **name + phone only**, never the device contact
  // id. An unusable number — missing, landline, foreign, mistyped length — takes the RFC's `null`
  // path for the phone: no error state, purchase never blocked. The contact's name is still worth
  // keeping, so the pick prefills it and the card stays on the manual form for the phone.
  const handleDeviceContactPicked = (index: number, name?: string, mobile?: string) => {
    const normalized = normalizeIraqiPhone(mobile ?? '');
    if (!normalized.ok) {
      // TODO(TZ-CO-11): emit device_picker_result { result: 'nophone' } (normalized.error says why)
      void fillHolderFromContact(index, { name }, 'device');
      return;
    }
    // The field holds national digits beside a static `+964`; `transformTicketHoldersData` puts it
    // back through the same normalizer for the E.164 form at submit, so nothing re-derives it here.
    void fillHolderFromContact(index, { name, phone: normalized.national }, 'device');
    // TODO(TZ-CO-11): emit device_picker_result { result: 'granted' }
  };

  // Confirmed pick from the saved-holders sheet. The contract stores E.164 and the field wants
  // national digits, so the phone goes back through `normalizeIraqiPhone` rather than having the
  // `+964` hand-sliced off. `useSavedHolders` only offers holders that normalize, so the guard is
  // for the case where that ever stops being true — it degrades to a name-only prefill.
  const handleSavedHolderConfirmed = (holder: SavedHolder) => {
    const index = sheetHolderIndex;
    setSheetHolderIndex(null);
    if (index === null) return;

    const normalized = normalizeIraqiPhone(holder.phone);
    void fillHolderFromContact(
      index,
      { name: holder.name, phone: normalized.ok ? normalized.national : undefined },
      'saved',
    );
  };

  // One entry point for "pick a contact"; the resolved source decides which picker opens.
  const handleChooseContact = (index: number) => {
    if (contactSource === 'device') {
      // TODO(TZ-CO-11): emit device_picker_opened
      hylideBridge?.choosePhoneContact?.({
        success: ({ name, mobile }) => handleDeviceContactPicked(index, name, mobile),
        fail: () => {
          // Permission denied / cancelled / bridge error → keep whatever was entered manually.
          // No error UI, purchase never blocked.
          // TODO(TZ-CO-11): emit device_picker_result { result: 'denied' | 'cancelled' } (classify from err)
        },
      });
      return;
    }

    // savedHolders (web/default). The list was fetched when the page loaded, so this is a cache
    // hit; if it is still in flight the sheet shows its skeleton.
    // TODO(TZ-CO-11): emit saved_holders_sheet_opened
    setSheetHolderIndex(index);
  };

  return (
    <>
      <form id='ticket-holder-hook-form' onSubmit={handleSubmit(onSubmit)}>
        {tickets.map((ticket, index) => (
          <HolderCard
            key={index}
            ticket={ticket}
            index={index}
            control={control}
            errors={errors}
            dialingCode={dialingCode}
            contactSource={contactSource}
            canPickContact={canPickContact}
            isCollapsed={Boolean(collapsed[index])}
            // Only the first card, and only while its own contact is still in flight. The other
            // cards do not depend on the response, so they stay usable immediately.
            isLoading={index === 0 && isAwaitingMe}
            onSaveDetails={() => void collapseIfValid(index)}
            onManualEdit={() => markHolderEditedByHand(index)}
            onEdit={() => setCollapsed((prev) => ({ ...prev, [index]: false }))}
            onChooseContact={() => handleChooseContact(index)}
          />
        ))}
      </form>

      {/* Outside the `form`: the sheet's own buttons must never participate in checkout submit. */}
      <SavedHoldersSheet
        isOpen={sheetHolderIndex !== null}
        onClose={() => setSheetHolderIndex(null)}
        holders={savedHolders}
        isLoading={isLoadingSavedHolders}
        onConfirm={handleSavedHolderConfirmed}
      />
    </>
  );
};
